import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuthContext } from '@/contexts/AuthContext';

export interface Payment {
  id: string;
  order_id: string | null;
  nowpayments_id: string;
  user_id: string;
  user_name: string;
  product_name: string;
  currency: string;
  amount: number;
  pay_amount: number;
  pay_address: string;
  status: 'waiting_payment' | 'confirming' | 'confirmed' | 'finished' | 'failed' | 'refunded';
  raw_payload: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

export interface PaymentStats {
  total_volume: number;
  total_transactions: number;
  avg_transaction: number;
  success_rate: number;
  by_currency: { name: string; value: number; fill: string }[];
  monthly: { month: string; volume: number }[];
}

const CURRENCY_COLORS: Record<string, string> = {
  BTC: '#f7931a',
  USDT: '#26a17b',
  ETH: '#627eea',
  SOL: '#9945ff',
  USDC: '#2775ca',
};

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function computeStats(payments: Payment[]): PaymentStats {
  const finished = payments.filter(p => p.status === 'finished' || p.status === 'confirmed');
  const total_volume = finished.reduce((s, p) => s + Number(p.amount), 0);
  const total_transactions = payments.length;
  const avg_transaction = total_transactions > 0 ? Math.round((total_volume / Math.max(finished.length, 1)) * 10) / 10 : 0;
  const success_rate = total_transactions > 0 ? Math.round((finished.length / total_transactions) * 1000) / 10 : 0;

  // By currency
  const currencyMap: Record<string, number> = {};
  finished.forEach(p => { currencyMap[p.currency] = (currencyMap[p.currency] ?? 0) + Number(p.amount); });
  const currencyTotal = Object.values(currencyMap).reduce((a, b) => a + b, 0) || 1;
  const by_currency = Object.entries(currencyMap)
    .map(([name, val]) => ({ name, value: Math.round((val / currencyTotal) * 100), fill: CURRENCY_COLORS[name] ?? '#6b7280' }))
    .sort((a, b) => b.value - a.value);

  // Monthly (last 6 months)
  const now = new Date();
  const monthly: { month: string; volume: number }[] = [];
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const month = MONTH_LABELS[d.getMonth()];
    const vol = finished
      .filter(p => {
        const pd = new Date(p.created_at);
        return pd.getFullYear() === d.getFullYear() && pd.getMonth() === d.getMonth();
      })
      .reduce((s, p) => s + Number(p.amount), 0);
    monthly.push({ month, volume: Math.round(vol) });
  }

  return { total_volume: Math.round(total_volume), total_transactions, avg_transaction, success_rate, by_currency, monthly };
}

export function usePayments() {
  const { user } = useAuthContext();
  const [payments, setPayments] = useState<Payment[]>([]);
  const [stats, setStats] = useState<PaymentStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPayments = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    setLoading(true);
    const { data, error: fetchError } = await supabase
      .from('payments')
      .select('*')
      .order('created_at', { ascending: false });
    if (fetchError) {
      setError(fetchError.message);
    } else {
      const list = (data ?? []) as Payment[];
      setPayments(list);
      setStats(computeStats(list));
    }
    setLoading(false);
  }, [user]);

  useEffect(() => { fetchPayments(); }, [fetchPayments]);

  return { payments, stats, loading, error, refetch: fetchPayments };
}
